import Box from '@components/base/Box'
import Tooltip from '@components/tootilp/Tooltip'
import { getParentBg, useTheme } from '@libs/useTheme'
import {
  PASSWORD_PATTERN,
  TEXT_PATTERN,
  XSS_PATTERN,
} from '@libs/Utility/validation'
import { Maybe } from 'graphql/jsutils/Maybe'
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { MdVisibility, MdVisibilityOff } from 'react-icons/md'
import { isReadable } from 'tinycolor2'
import { BaseInputProps } from './BaseInputProps'
import { getDefaultValidations, validate } from './Validations'

export interface TextInputProps extends Omit<BaseInputProps, 'error'> {
  /**
   * The type of the input.
   */
  type?: 'text' | 'password' | 'email' | 'tel' | 'number'
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onBlur?: () => void
  border?: 'border' | 'border-none'
  max?: number
  min?: number
  textColour?: string
  name?: string
  labelBgColour?: string
  show?: boolean
  customClassLabel?: string
  autoComplete?: string
  setError?: (message: string, testId: string) => void
  ignoreDefaultValidations?: boolean
  forceLabel?: boolean
  error?: string | ReactNode
}

/**
 * Primary UI component for text input
 */
export const TextInput = ({
  label,
  placeholder,
  value = '',
  onChange,
  className,
  hint,
  validations,
  required,
  type = 'text',
  border = 'border',
  onKeyDown,
  onBlur,
  max,
  min,
  textColour,
  disabled,
  labelBgColour,
  testid,
  name,
  tooltip,
  show = true,
  customClassLabel = '',
  error = undefined,
  setError,
  autoComplete,
  ignoreDefaultValidations = false,
  variation = 'v1',
  forceLabel = false,
  disableInlineErrors = false,
}: TextInputProps) => {
  const fieldTypeValidations = ignoreDefaultValidations
    ? validations
    : getDefaultValidations(type, validations)

  let errorMessage = error ?? validate(value, fieldTypeValidations)
  // if (errorMessage)
  // console.log({ errMessage: errorMessage, fieldTypeValidations })

  const [showTooltip, setShowTooltip] = useState(true)
  const [showPassword, setShowPassword] = useState<boolean>(false)

  useEffect(() => {
    // Autohide tooltip if the input is not required.
    if (tooltip?.toLowerCase().includes('require') && !required)
      setShowTooltip(false)
  }, [tooltip, required])

  // useEffect(() => {
  //   if (errorMessage) console.log({ errorMessage, fieldTypeValidations })
  // }, [errorMessage])

  if (value === null) value = ''

  const generateStyles = useMemo(() => {
    let base = `transition-all outline-none`

    const errorStyles = errorMessage ? 'text-red-500 border-red-500' : ''
    const disabledStyles = disabled ? '!text-gray-500' : ''
    let textStyles = 'text-sm placeholder-gray-500'
    let positionStyles = 'px-4 py-4'
    let sizeStyles = 'w-full'

    if (variation === 'v1') {
      base = `bg-transparent rounded-2xl`
    }

    if (variation === 'v2') {
      base = `!bg-[#F7E5E4] rounded-none !border-none`
      textStyles = 'text-sm placeholder-black font-semibold'
    }

    const payload = `${base} ${textStyles} ${positionStyles} ${border} ${errorStyles} ${disabledStyles} ${sizeStyles} ${className}`
    return payload
  }, [border, disabled, errorMessage, variation])

  const input = useMemo(
    () => (
      <input
        data-testid={testid ?? null}
        required={required}
        value={value}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        onChange={(e) => {
          const val = e.target.value
          errorMessage = validate(val, fieldTypeValidations)
          // console.log({ errorMessage, target: val, fieldTypeValidations })
          // setErrorMessage(errorMsg ?? '')
          const isInvalid = errorMessage !== ''
          if (!!setError && typeof errorMessage === 'string')
            setError(errorMessage, testid ?? label ?? 'unknown')
          if (!!onChange) onChange(val, isInvalid)
        }}
        max={max}
        disabled={disabled}
        min={min}
        type={showPassword ? 'text' : type}
        placeholder={placeholder}
        autoComplete={autoComplete ?? label}
        id={label}
        className={generateStyles}
        style={{ color: textColour }}
      />
    ),
    [
      testid,
      required,
      value,
      onKeyDown,
      onBlur,
      max,
      disabled,
      min,
      type,
      placeholder,
      autoComplete,
      label,
      generateStyles,
      textColour,
      errorMessage,
      fieldTypeValidations,
      showPassword,
    ]
  )

  const labelRef = useRef<HTMLLabelElement>(null)

  const showLabel = useMemo(
    () => (label && variation === 'v1') || forceLabel,
    [label, variation, forceLabel]
  )

  if (!show) return <></>
  return (
    <label
      className={['flex flex-col my-4', className].join(' ')}
      style={{ color: textColour }}
    >
      {showLabel && (
        <span
          ref={labelRef}
          className={`
            absolute text-sm mb-2 transition-all z-10
            ${variation === 'v1' ? 'ml-2 !backdrop-blur-md' : ''}  
            ${required ? (variation === 'v1' ? 'pl-2' : '') : 'px-2'} 
            ${errorMessage ? 'text-red-500' : ''}`}
          style={{
            transform:
              variation === 'v1' ? 'translateY(-50%)' : 'translateY(-100%)',
          }}
        >
          <Tooltip
            show={showTooltip}
            tooltip={tooltip}
            className={`flex flex-col`}
          >
            <span
              className={`z-10 
                ${variation === 'v2' ? 'text-black font-semibold' : ''}
                ${customClassLabel} 
                ${required ? "after:content-['*'] after:mx-2 after:text-red-500" : ''}`}
            >
              {label}
            </span>
          </Tooltip>
        </span>
      )}
      <span className="relative">
        {input}
        {type === 'password' && (
          <button
            type="button"
            className="absolute right-2 top-[50%] -translate-y-[50%] text-xl items-center pr-3 cursor-pointer"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <MdVisibility /> : <MdVisibilityOff />}
          </button>
        )}
      </span>
      {errorMessage && !disableInlineErrors && (
        <Box
          ignoreTheme
          textColour="foregroundTextColour"
          className="transition-all text-red-500 text-xs font-bold mt-1"
        >
          {errorMessage}
        </Box>
      )}
      {hint && (
        <span className="mt-1 mb-4 ml-2 text-xs" style={{ color: textColour }}>
          {hint}
        </span>
      )}
    </label>
  )
}
